import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import {
  CM_IN_FOOT,
  FOOT_IN_INCHES,
  inchesToCm,
  poundsToKg
} from "src/lib/unitConversion";

/**
 * Resolves the id of the hidden, `[only-logic]` weight question a
 * `[body-metrics-field]` question is paired with via its
 * `[weight-field-ref="<ref>"]` keyword (see BodyMetricsInput). Answers are
 * keyed by field id, but the CMS links fields by their stable `ref`, so this
 * translates one into the other.
 */
export function findPairedWeightFieldId(
  fields: QuestionnaireField[],
  weightFieldRef: string | undefined
): string | undefined {
  if (!weightFieldRef) {
    return undefined;
  }

  return fields.find((field) => field.ref === weightFieldRef)?.id;
}

export type BodyMetricsFieldStatus = "valid" | "missing" | "out-of-range";

export interface BodyMetricsValidationResult {
  heightCm?: number;
  weightKg?: number;
  heightStatus: BodyMetricsFieldStatus;
  weightStatus: BodyMetricsFieldStatus;
}

/**
 * Validates the raw feet/inches/pounds input against the same bounds
 * HeightInput/WeightInput each enforce standalone (50-300cm, 2-300kg), plus
 * the one thing a single-field standalone input gets for free and this
 * combined field doesn't: weight has no answer key of its own that
 * QuestionnaireStep's generic required-check watches, so a missing weight
 * has to be reported here to block Continue (`weightStatus: "missing"`)
 * whenever a paired weight field exists at all (`hasWeightField`).
 */
export function validateBodyMetrics(params: {
  feet: number;
  inches: number;
  pounds: number;
  hasWeightField: boolean;
}): BodyMetricsValidationResult {
  const heightCmValue = inchesToCm(
    params.feet * FOOT_IN_INCHES + params.inches
  );
  const isHeightProvided = heightCmValue >= CM_IN_FOOT && !isNaN(heightCmValue);
  const heightStatus: BodyMetricsFieldStatus = !isHeightProvided
    ? "missing"
    : heightCmValue < 50 || heightCmValue > 300
      ? "out-of-range"
      : "valid";

  const weightKgValue = poundsToKg(params.pounds);
  const isWeightProvided = params.pounds > 0 && !isNaN(weightKgValue);
  const weightStatus: BodyMetricsFieldStatus = !isWeightProvided
    ? params.hasWeightField
      ? "missing"
      : "valid"
    : weightKgValue < 2 || weightKgValue > 300
      ? "out-of-range"
      : "valid";

  return {
    heightCm: isHeightProvided ? heightCmValue : undefined,
    weightKg: isWeightProvided ? weightKgValue : undefined,
    heightStatus,
    weightStatus
  };
}
